import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { handleActionError, actionSuccess } from '../../utils/actionFee';
import { Landmark } from 'lucide-react';
import { PayloadFieldsForm, toPayload } from './PayloadFieldsForm';
import { IssuedChecksList } from './IssuedChecksList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-treasury-check`;

const Holding = ({ h, onTransfer, busy }) => {
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  return (
    <div className="bg-slate-900 rounded-lg p-4" data-testid={`tc-holding-${h.network}`}>
      <div className="flex flex-wrap justify-between gap-2 text-sm">
        <p className="text-slate-400">{({ sepolia: 'Sepolia (test)', mainnet: 'Ethereum mainnet', base: 'Base', arbitrum: 'Arbitrum' })[h.network] || h.network}: <span className="text-white font-semibold" data-testid={`tc-balance-${h.network}`}>{h.balance.toLocaleString()} {['sepolia', 'base'].includes(h.network) ? 'BETH' : 'BTCK'}</span> <span className="text-slate-500">(${h.balance.toLocaleString()} face)</span></p>
        <a href={h.explorer_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 underline">Token on explorer</a>
      </div>
      {h.series && <p className="text-xs text-slate-500 mt-1">Reference: Treasury bond {h.series.cusip} · {h.series.security_term} · reopening auction {h.series.auction_date}</p>}
      {h.balance > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Recipient wallet or subscriber email" data-testid={`tc-transfer-to-${h.network}`} className="flex-1 min-w-[14rem] bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
          <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="Amount" data-testid={`tc-transfer-amount-${h.network}`} className="w-32 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
          <button onClick={() => onTransfer(h.network, to, Number(amount), () => { setTo(''); setAmount(''); })} disabled={busy || !to || !(Number(amount) > 0)}
            data-testid={`tc-transfer-btn-${h.network}`} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm disabled:opacity-40">{busy ? 'Sending...' : 'Transfer'}</button>
        </div>
      )}
    </div>
  );
};

const MintForm = ({ network, busy, onMint, available }) => {
  const [amount, setAmount] = useState('');
  const [to, setTo] = useState('');
  const [p, setP] = useState({});
  return (
    <div className="bg-slate-900 rounded-lg p-4" data-testid={`tc-sub-mint-${network}`}>
      <p className="text-sm text-white font-medium">Mint a Treasury Check</p>
      <p className="text-xs text-slate-500 mb-2">Funded by a draw on your signed credit line (repaid under your Promissory Note). Available: <span data-testid={`tc-sub-available-${network}`}>${Number(available || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></p>
      <div className="flex flex-wrap gap-2">
        <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="Amount (USD)" data-testid={`tc-sub-mint-amount-${network}`} className="w-40 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Payee (blank = my wallet)" data-testid={`tc-sub-mint-to-${network}`} className="flex-1 min-w-[12rem] bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
      </div>
      <PayloadFieldsForm value={p} onChange={setP} prefix={`tc-sub-${network}`} />
      <button onClick={() => onMint(network, { amount: Number(amount), to: to.trim(), ...toPayload(p) }, () => { setAmount(''); setTo(''); setP({}); })}
        disabled={busy || !(Number(amount) > 0)} data-testid={`tc-sub-mint-btn-${network}`}
        className="mt-3 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm disabled:opacity-40">{busy ? 'Minting...' : 'Mint Treasury Check'}</button>
    </div>
  );
};

export const TreasuryCheckCard = ({ token }) => {
  const [d, setD] = useState(null);
  const [checks, setChecks] = useState([]);
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/holdings`, h).then(r => setD(r.data)).catch(() => {});
    axios.get(`${API}/checks`, h).then(r => setChecks(r.data.checks)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const mint = async (network, body, done) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/mint`, { network, ...body }, auth);
      actionSuccess(r.data); done(); load();
    } catch (err) { handleActionError(err, token, 'Mint failed'); }
    finally { setBusy(false); }
  };
  const transfer = async (network, to, amount, done) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/transfer`, { network, to, amount }, auth);
      actionSuccess(r.data); done(); load();
    } catch (err) { handleActionError(err, token, 'Transfer failed'); }
    finally { setBusy(false); }
  };
  const removeTest = async (c) => {
    try { const r = await axios.delete(`${API}/checks/${c.check_id}`, auth); toast.success(r.data.message); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };
  if (!d?.holdings?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6" data-testid="treasury-check-card">
      <h3 className="text-white font-semibold flex items-center gap-2"><Landmark className="w-5 h-5 text-emerald-400" /> BETH Treasury Checks</h3>
      <p className="text-sm text-slate-400 mt-1 mb-4">Digital treasury checks issued in BETH USD (BETH, the dollar-for-dollar stablecoin; legacy BTCK on other networks) through the BETHCOIN Exchange Market Treasury Reserve Wallet, $1 face each. The Treasury bond CUSIP is reference data, not ownership of a bond. <a href="/pas/exchange-agreements" className="text-emerald-400 underline" data-testid="tc-exchange-agreements-link">Exchange Agreements</a></p>
      <div className="space-y-3">
        {d.holdings.map(h => (
          <React.Fragment key={h.network}>
            <Holding h={h} busy={busy} onTransfer={transfer} />
            {h.version >= 2 && h.network === 'base' && (d.can_mint ? <MintForm network={h.network} busy={busy} onMint={mint} available={d.available_credit_usd} />
              : <p className="text-xs text-amber-300 bg-slate-900 rounded-lg p-3" data-testid={`tc-sub-mint-blocked-${h.network}`}>Minting Treasury Checks: {d.mint_blocked_reason}.{d.mint_blocked_reason?.startsWith('Sign') && <a href="/pas/credit-agreement" className="underline ml-1">Review &amp; sign</a>}</p>)}
          </React.Fragment>
        ))}
        <IssuedChecksList checks={checks} printPath={(id) => `/pas/treasury-check/${id}`} testid="tc-my-checks" onDelete={removeTest} />
      </div>
    </div>
  );
};
